// Single source of truth for site content. Replace any `image` path to swap visuals.
// All deck artwork lives under /public/art/ (extracted from CBB DECK).

export const SITE = {
  title: "Chains Beneath Bakhtak",
  tagline: "Break the Illusion",
  shortPitch:
    "Across Bakhtak's nine nations, every culture wears a mask. The peace between them is not harmony — it is performance. Season One begins where three wanderers stumble into a truth the rest of the world is determined to bury.",
  longPitch:
    "Each nation is buried beneath a unified identity, carefully engineered over 300 years of dissolution and political choreography. What humanity sees as 'culture' is really the afterimage of centuries of manipulation: manufactured societies set to play a part in a plan designed to rewrite the world.",
  creator: {
    name: "Isaiah M. Willis-Dean",
    role: "Creator · Story Architect · Director of Vision",
    email: "Chainsbeneathbakhtak@icloud.com",
    phone: "267-993-1142",
  },
  comparableTitles: ["Attack on Titan", "One Piece", "Castlevania"],
  socials: {
    discord: "",
    twitter: "https://twitter.com/Chains_bb",
    tiktok: "https://www.tiktok.com/@chainsbeneathbakhtak",
    instagram: "",
    reddit: "",
  },
};

// ---- Visual assets (deck artwork) ----
export const ART = {
  logo: "/art/deck_p01.jpg",
  hero: "/art/hero_new.jpg",
  heroPosition: "center 35%",
  nationsBanner: "/art/nations_banner.jpg",
  worldState: "/art/deck_p03.jpg",
  pilotCover: "/art/deck_p22_card4.jpg", // Shadows of Namtaria poster
};

// ---- Art direction posters ----
export const SAGA_POSTERS = [
  { id: "art-01", title: "Bakhtak", image: "/art/art_01.jpg", objectPosition: "center top" },
  { id: "art-02", title: "Season One — The Living Forest", image: "/art/art_02.jpg" },
  { id: "art-03", title: "Specula Court", image: "/art/art_03.jpg" },
  { id: "art-04", title: "Venus — Tamer of Vine Serpents", image: "/art/art_04.jpg" },
  { id: "art-05", title: "Crown II — The Judgment Unit", image: "/art/art_05.jpg" },
];

// ---- The Nine Nations (canonical world bible) ----
// Size & population rankings: 1 = largest, 9 = smallest.
export const NATIONS = [
  {
    slug: "arathia",
    name: "Arathia",
    epithet: "People of Apollo",
    motto: "The body is a temple — prove it.",
    virtue: "Encouragement",
    flaw: "Perfection",
    size: 1,
    population: 1,
    aesthetic: "Marble headlands · sun-bleached arenas · vine-valleys",
    power: "Push — native force-emitting pores let trained fighters project concussive bursts from limbs and torso.",
    culture:
      "Sun-blessed coast-spanning empire that believes health equals divinity. Arenas double as temples; every contest is a rite to prove the body worthy of Apollo's light. Children are raised in communal gymnasia where weakness is corrected, not pitied.",
    land: "Warm Mediterranean-like western littoral — marble-quarried headlands, vine-valleys, grain-belts.",
    economy:
      "Exports elite athletes, guards-for-hire, scented oils, and marble statuary. Technologically behind Zar but feared for ubiquitous combat-readiness.",
    summary:
      "Sun-blessed coast-spanning empire. Health is divinity, arenas are temples, and every contest is a rite to Apollo's light. Encouragement raises every Arathian — perfection breaks them.",
    accent: "#D4AF68",
    image: "/art/nation_arathia.jpg",
    tags: ["Empire", "Arenas", "Apollo"],
    season: "Season Two",
  },
  {
    slug: "namtaria",
    name: "Namtaria",
    epithet: "Children of Namtar",
    motto: "Detach. Endure. Obey.",
    virtue: "Emotional Intelligence",
    flaw: "Emptiness",
    size: 5,
    population: 5,
    aesthetic: "Black-sand ziggurats · obsidian-veined rivers · light-less orb-vault catacombs",
    power: "Orb-tethered immortality and spectral demon-projection; elite assassins animate corpses or shadow-husks.",
    culture:
      "A Babylon-echoing desert theocracy that severs the soul into a crystal orb at age 14 — emotions slowly purged after, life bound to a demon-servitor. Ziggurats are tiered by degree of detachment.",
    land: "South-west black-sand deserts cut by twin obsidian-veined rivers; beneath the cities sprawl light-less Orb-Vault Catacombs.",
    economy:
      "Thrives on mercenary legions, bounty-guilds, and paid assassinations. Also trades enchanted crystal and volcanic glasswork.",
    summary:
      "A theocracy that cuts the soul out at adulthood and binds the body to a demon-servitor. Their emotional intelligence is monstrous — and so is the emptiness it leaves behind.",
    accent: "#B33A8C",
    image: "/art/nation_namtaria.jpg",
    tags: ["Theocracy", "Demon-Bound", "Assassins"],
    season: "Season Three",
  },
  {
    slug: "specula",
    name: "Specula",
    epithet: "The Mirror Nation",
    motto: "Fade into element. Become the saviour.",
    virtue: "Heroism",
    flaw: "Naivety",
    size: 2,
    population: 3,
    aesthetic: "Alpine glaciers · mirror-lit citadels · hot-spring terraces",
    power: "Element-channelled mirror-staves — fire-storms, rock-walls, avalanches, tidal-floods.",
    culture:
      "Alpine hero-state. Vows of service are reflected every dawn in civic plaza mirrors. To fade into pure element is honoured as the highest heroic death — and every child is raised on tales that they, too, may die a saviour.",
    land: "Vast frozen mountain-mantle in the far-north; glaciers rimmed by enchanted hot-spring terraces and mirror-lit citadels.",
    economy:
      "Prosperous thanks to Zar-backed trade and their own elemental civil-engineering and peace-keeper legions.",
    summary:
      "An alpine hero-state where vows are sworn into mirrors and dissolving into pure element is the honoured death. Heroism shines bright; naivety lets others write the script.",
    accent: "#9EA9B4",
    image: "/art/nation_specula.jpg",
    tags: ["Alpine", "Mirror-Staves", "Heroes"],
    season: "Later Seasons",
  },
  {
    slug: "zar",
    name: "Zar",
    epithet: "Sky-Lords, Masters of Perception",
    motto: "Perception is power.",
    virtue: "Supremacy",
    flaw: "False Identity",
    size: 3,
    population: 2,
    aesthetic: "Drifting sky-terraces · gravitic mirrors · masked courts",
    power: "Secret matter-manipulation and life-force drainers; famed for unbreakable weapons and the ability to reshape stone or metal mid-construction.",
    culture:
      "Born of the Third Sundering's sky-table. A floating oligarchy where truth ≠ power — perception is power. Court children are schooled in rhetoric, illusion-craft, and masked politics from their first words.",
    land: "Sky-Terraces drift on gravitic mirrors above the mid-continent scar-basin. Temperate gardens above, strip-mined scars below.",
    economy:
      "True world-hegemon: controls sky-gates, fleets, and tech-patents; shadow-hires Namtarian blades and Therakan muscle.",
    summary:
      "Floating oligarchy where perception, not truth, is power. Zar writes the script every other nation performs — supremacy worn as a false face.",
    accent: "#3B6BA8",
    image: "/art/nation_zar.jpg",
    tags: ["Sky-Capital", "Perception", "Hegemon"],
    season: "Season Two",
  },
  {
    slug: "khepera",
    name: "Khepera",
    epithet: "Children of the Sacred Birds",
    motto: "Write it true; it will outlive us.",
    virtue: "Knowledge",
    flaw: "Overconfidence",
    size: 6,
    population: 4,
    aesthetic: "Obelisk-libraries · pyramid archives · ibis & falcon temple-aviaries",
    power: "Glyphic healing arts and incantation-wards; some forbidden necrotic script-mancers hide in the deep libraries.",
    culture:
      "Semi-avian scribal nation; a river-oasis civilisation that worships written truth as the immortality of memory.",
    land: "Eastern desert-river cradle lined with monumental obelisk-libraries, pyramid archives, and temple-aviaries of ibis, falcon, and owl.",
    economy:
      "Lives on pilgrimage, healing-trade, and rare manuscript licensing. Chooses self-sufficiency over imperial wealth.",
    summary:
      "Semi-avian scribes who treat written truth as immortality. Their knowledge is unmatched — and so is the overconfidence that comes with reading too many of your own histories.",
    accent: "#C5A059",
    image: "/art/nation_khepera.jpg",
    tags: ["Desert", "Scribes", "Glyph-Magic"],
    season: "Season One",
  },
  {
    slug: "verdani",
    name: "Verdani",
    epithet: "The Green-Born",
    motto: "A lie is a sour vibration in the soil.",
    virtue: "Truth",
    flaw: "Stagnation",
    size: 8,
    population: 6,
    aesthetic: "Living canopy-homes · hanging vine-bridges · river-veined Ring-Forest",
    power: "Photosynthetic regeneration and vine-limb manipulation; natural lie-sense felt as a sour vibration in the soil.",
    culture:
      "Arboreal truth-speakers. Radical honesty is law — lying is seen as a spiritual rot. Settlements are woven from living canopy-trees and hanging vine-bridges above river-veins.",
    land: "Central-continent Great Ring-Forest encircled by river-delta; the north side flooded into the Un-Crossable Lake-Sea after the Fifth Sundering.",
    economy:
      "Mostly closed-loop barter of food, fibre, and herb-remedies. Exports little; prizes harmony over coin.",
    summary:
      "Arboreal truth-speakers whose canopy-homes breathe and whose soil hears lies. Truth is their virtue and their cage — they refuse to evolve.",
    accent: "#4A7C3A",
    image: "/art/nation_verdani.jpg",
    tags: ["Forest", "Honesty", "Vine-Combat"],
    season: "Season One",
  },
  {
    slug: "akira",
    name: "Akirans",
    epithet: "The Blood-Touched",
    motto: "We are what they could not unmake.",
    virtue: "Burden",
    flaw: "Trauma",
    size: null,
    sizeLabel: "No Homeland",
    population: 9,
    aesthetic: "Torii-gate sanctuaries · blood-ink calligraphy · haiku rituals",
    power: "Unique to each lineage: blood-crystal weapon-smithing, crimson sigil-wards, healing seals, personalised blood-arts.",
    culture:
      "Once had a cradle-land — now lost to purges. They live as scattered shrine-villages in mountains and ruins. Japanese-inspired elegance: torii sanctuaries, blood-ink calligraphy, haiku rituals.",
    land: "Now only hidden enclaves in foreign forests, caves, or Therakan slums. Legends whisper the drowned Seal-Spirit beneath Verdani's lake once guarded their ancestral ground.",
    economy:
      "Impoverished diaspora — survives on black-market blood-crystal trade, fine red-crystal jewellery and blades, and mercenary healers.",
    summary:
      "Scattered survivors of an erased genocide. Each lineage carries a unique blood-art — and the burden of being history's quietest disaster.",
    accent: "#A8323A",
    image: "/art/nation_akira.jpg",
    imageObjectPosition: "center bottom",
    tags: ["Diaspora", "Blood-Craft", "Lineages"],
    season: "Season One",
  },
  {
    slug: "theraka",
    name: "Theraka",
    epithet: "The Beast-Clans",
    motto: "Stronger claws win the lagoon.",
    virtue: "Strength",
    flaw: "Ignorance",
    size: 4,
    population: 7,
    aesthetic: "Steaming lagoons · bone-strewn dens · cavern-forts around the King's Lagoon Arena",
    power: "Theriomorphic bat-hybrids — serpent-bat venoms, feline-bat claws, rhino-bat chargers, ape-bat climber-brutes. Addiction to the shift erodes humanity over time.",
    culture:
      "Swamp-born predator zealots who revere unleashing the bat-beast within. Packs are ruled by the strongest claw or fang; festivals are ritual hunts and blood-arena games.",
    land: "Far-south steaming lagoons and sink-vales birthed by the Second Sundering; bone-strewn dens and cavern-forts ring the fabled King's Lagoon Arena.",
    economy:
      "Poor in goods, rich in fear. Hires out as Zar's shock-troops, sells hunting trophies and bone-crafts, and lives off tribute wrung from neighbours.",
    summary:
      "Swamp-born predator zealots who unleash the bat-beast within. Their strength is feared across Bakhtak — and so is their willful ignorance of everything beyond the next hunt.",
    accent: "#7C2C2C",
    image: "/art/nation_theraka.jpg",
    tags: ["Swamp", "Theriomorphs", "Shock-Troops"],
    season: "Later Seasons",
  },
  {
    slug: "abyssari",
    name: "Abyssari",
    epithet: "The Deep Ones",
    motto: "The vision is already written.",
    virtue: "Foresight",
    flaw: "Fear",
    size: 7,
    population: 8,
    aesthetic: "Bioluminescent deep-trench cities · black-coral spires · pressure-warded basalt",
    power: "Localised gravity control for crushing leaps and terrain-warping strikes; visions of the future treated as inescapable law — gift and curse alike.",
    culture:
      "Bioluminescent deep-sea isolationists ruled by clairvoyant seers. The whole civilisation is haunted by prophecy-fatalism. They worship slow whale-chant hymns, pearl mosaics, and trance festivals.",
    land: "Twin hidden abyssal trenches — one below the northern Speculan shelf, one beneath the southern Therakan coast — cities of black-coral and pressure-warded basalt.",
    economy:
      "Dependent on Zar secrecy-pacts. Exports pearls, abyss-crystals, and bio-luminescent dyes. The real currency, though, is prophecy — sold to Zar courts.",
    summary:
      "Bioluminescent deep-trench prophets who sell visions to Zar's masked court. Foresight is their gift and their cage — they fear nothing more than a future they cannot already see.",
    accent: "#6F4FB5",
    image: "/art/nation_abyssari.jpg",
    tags: ["Abyss", "Prophecy", "Gravity-Arts"],
    season: "Later Seasons",
  },
];

// ---- Cast ----
export const CHARACTERS = [
  {
    slug: "thamir",
    name: "Thamir",
    epithet: "The Fool Who Lights the World",
    nation: "Khepera",
    role: "Curious wanderer · Accidental catalyst",
    power: "Untrained Light-Magic · Horus Text resonance",
    line: "He believes in legends long after everyone else stopped.",
    bio: "Thamir is the boy who can't fight, freezes under pressure, and apologizes mid-battle. But every time his friends should have died, he throws himself between danger and them — unlocking flickers of ancient light. He is the heart of the trio, and the spark that wakes a sealed magic the world has forgotten.",
    image: "/art/deck_p05.jpg",
    accent: "#C5A059",
  },
  {
    slug: "selura",
    name: "Selura",
    epithet: "The Root That Refuses to Break",
    nation: "Verdani",
    role: "Protector · Moral center · Emotional momentum",
    power: "Verdani body-vine combat · sunlight endurance · lie-sensing instincts",
    line: "She doesn't believe in destiny. She believes in doing the right thing — even when it hurts.",
    bio: "Stubborn, blunt, and burdened with the responsibility of keeping everyone alive. When raids begin and the desert turns lethal, Selura becomes the shield of the trio. She doesn't bend — she bleeds, stands, and buys time until her friends can stand with her.",
    image: "/art/deck_p06.jpg",
    accent: "#4A7C3A",
  },
  {
    slug: "shion",
    name: "Shion",
    epithet: "The Stray Blade",
    nation: "Akirans",
    role: "Anti-hero · Unpredictable ally · Emotional core",
    power: "Blood-craft (imperfect) · trauma-hardened reflexes · predatory instincts",
    line: "I don't join. I survive.",
    bio: "A boy raised for violence, performing apathy to hide a deep fracture inside him. He joins the trio not out of loyalty but boredom — and yet he shields them without hesitation. He mocks kindness, denies fear, kills efficiently — and still sleeps near the campfire instead of walking away.",
    image: "/art/deck_p07.jpg",
    accent: "#A8323A",
  },
  {
    slug: "kale",
    name: "Kale",
    epithet: "The Pressure of Duty",
    nation: "Namtaria",
    role: "Antagonist encounter · Transport guard",
    power: "Pressure-force demon art (Dance of the Giant) · enhanced durability · Namtarian death-detachment",
    line: "Some enemies cannot simply be killed.",
    bio: "A Namtarian transport guard escorting Akiran prisoners west toward Arathia. Calm, disciplined, and unnervingly pragmatic. His demon art unleashes crushing waves of invisible pressure that flatten the battlefield — forcing the trio to fight together for the first time.",
    image: "/art/deck_p08.jpg",
    accent: "#B33A8C",
  },
  {
    slug: "shuyami",
    name: "Shuyami",
    epithet: "Rebellion Bleeds",
    nation: "Akirans",
    role: "Primary antagonist · Blood-Chain Morphic",
    power: "Living blood-chains extending from his body · berserker rage · instinct-driven combat",
    line: "We take what we were denied.",
    bio: "Once a rejected experiment of the lower pits, he escaped carrying nothing but rage and a body forged for torment. His blood-chains lash out with animal precision. His role is to force the protagonists out of their innocence, revealing the monstrous human cost of Zar's interference long before Zar's face ever appears.",
    image: "/art/deck_p09.jpg",
    accent: "#8A1C1C",
  },
  {
    slug: "venus",
    name: "Venus",
    epithet: "Tamer of Vine Serpents",
    nation: "Verdani",
    role: "Verdani Exiled Son · Antagonist threat",
    power: "Vine supremacy · Master of Verdani vine arts · Commander of serpenting beasts",
    line: "The forest does not forgive.",
    bio: "Verdani's exiled son returned — a vine supremacist who commands serpenting beasts and treats the forest's mercy as a weakness. Status: Extreme Threat. Verdani territory under lockdown wherever he walks.",
    image: "/art/cast_venus.jpg",
    imageObjectPosition: "center 18%",
    accent: "#5BAE3C",
  },
  {
    slug: "auren",
    name: "Auren Vel-Selis",
    epithet: "The Quiet Grove",
    nation: "Verdani",
    role: "Healer · Wish-Flower Defender Leader · Selura's First Love",
    power: "Master Sunroot Healing · Twin Hip-Vines · Multi-Axe Juggle Combat · Verdani Daybloom Physiology",
    line: "Some roots grow deeper than destiny. Others break trying to hold it.",
    bio: "Gifted with the rare Daybloom lineage, Auren is one of the youngest master healers in Verdani memory. Gentle, patient, and admired by nearly everyone who meets him, he embodies the warmth and stability of the forest itself. Where Selura charges headfirst into change, Auren believes in preserving what matters and protecting those he loves. He becomes a living reminder of the life she could have chosen — and the heartbreak that comes when love and purpose grow in different directions.",
    image: "/art/cast_auren.jpg",
    accent: "#7CB142",
  },
];

// ---- Season + Episode roadmap ----
export const SEASONS = [
  {
    n: 1,
    title: "Verdani: A Forest Too Honest for Its Own Good",
    summary:
      "Thamir drags Selura into ancient ruins searching for a mythical text. Instead they unleash Shion — an Akiran runaway whose trauma has its own weather system. The trio forms. Borders burn. Caravans vanish. The season erupts in a brutal showdown against Shuyami.",
    image: "/art/deck_p11.jpg",
    episodes: [
      { n: 1, title: "The Mistake", logline: "Three strangers collide in the Verdani wilds when a reckless search for a forgotten text unleashes an Akiran survivor with a violent past." },
      { n: 2, title: "Roots & Lies", logline: "Humor and warmth mask deeper fractures. Wish Flowers emerge. Thamir uncovers truths about Khepera that don't match the stories. The world begins to feel… edited." },
      { n: 3, title: "What's Worth Protecting", logline: "Verdani reveals what it truly values — community, truth, survival — just as outside interference begins to surface." },
      { n: 4, title: "The Supremacy Attempt", logline: "Venus strikes. Outsiders become protectors. Respect is earned in blood and root." },
      { n: 5, title: "The Truth of Akira", logline: "Through Shuyami, the ideology behind the violence comes into focus. Shion confronts what he is — and what he could become." },
      { n: 6, title: "Break the Illusion", logline: "The final clash shatters the trio's understanding. This was never about a single threat. It's a system. The Sky Games approach." },
    ],
  },
  {
    n: 2,
    title: "The Sky Games",
    summary:
      "The nations of Bakhtak gather in Zar for a global tournament meant to celebrate unity. As rival champions clash, deeper political tensions surface. Selura, Thamir, and Shion travel toward Arathia, following clues linking Shion's captivity to a larger system of exploitation.",
    image: "/art/deck_p22_card7.jpg",
    episodes: [],
  },
  {
    n: 3,
    title: "Rebels of Bakhtak",
    summary:
      "Dorion joins Selura, Thamir, and Shion, defying his homeland. Their protection of Shion marks them as rebels. Relentlessly hunted by an Akiran tracking squad led by Oblivion, they journey toward Namtaria — where Seraphi waits in the shadows.",
    image: "/art/deck_p22_card4.jpg",
    episodes: [],
  },
];
